import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { streamTarotReading, logStaticReading, RateLimitError } from "../../Services/aiReading";
import { useSpeechContext } from "../../Services/Context/SpeechContext";
import { useLanguage } from "../../Services/Context/LanguageContext";
import StaticCardMeaning from "../StaticCardMeaning/StaticCardMeaning";
import { cardMeanings } from "../../Services/cardMeanings";
import "./ClarifyingReading.css";

interface CardRef {
  name: string;
  isReversed: boolean;
}

interface ClarifyingReadingProps {
  question: string;
  cardName: string;
  isReversed: boolean;
  image: string;
  originalCard: CardRef;
  previousClarifyingCards?: CardRef[];
  onDrawAgain?: () => void;
  onDrawClarifying?: () => void;
  aiEnabled?: boolean;
  rateLimitError?: string | null;
}

const ClarifyingReading = ({
  question,
  cardName,
  isReversed,
  image,
  originalCard,
  previousClarifyingCards,
  onDrawAgain,
  onDrawClarifying,
  aiEnabled = true,
  rateLimitError,
}: ClarifyingReadingProps) => {
  const [text, setText] = useState("");
  const [isComplete, setIsComplete] = useState(false);
  const topRef = useRef<HTMLDivElement>(null);
  // Freeze previous cards and language at mount time so re-renders don't restart the stream.
  const prevCardsRef = useRef(previousClarifyingCards);
  const { language, translate } = useLanguage();
  const langRef = useRef(language);
  const { speak } = useSpeechContext();

  useEffect(() => {
    const timer = setTimeout(() => {
      topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!aiEnabled) {
      setIsComplete(true);
      let active = true;
      const timer = setTimeout(() => {
        if (active) logStaticReading(cardName, isReversed, question, originalCard);
      }, 0);
      return () => {
        active = false;
        clearTimeout(timer);
      };
    }

    let cancelled = false;
    const controller = new AbortController();
    setText("");
    setIsComplete(false);

    const fetchReading = async () => {
      try {
        const generator = streamTarotReading(
          cardName,
          isReversed,
          question,
          originalCard,
          controller.signal,
          prevCardsRef.current,
          langRef.current,
          undefined,
          undefined,
          undefined,
          true,
        );
        for await (const chunk of generator) {
          if (cancelled) break;
          setText((prev) => prev + chunk);
        }
        if (!cancelled) setIsComplete(true);
      } catch (err) {
        if (cancelled) return;
        console.error("Clarifying reading error:", err);
        setText(
          err instanceof RateLimitError
            ? err.message
            : translate("readingError"),
        );
        setIsComplete(true);
      }
    };

    fetchReading();
    return () => {
      cancelled = true;
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardName, isReversed, question, originalCard, aiEnabled, translate]);

  // Auto-speak when reading is complete
  useEffect(() => {
    if (!isComplete) return;
    if (aiEnabled && text) {
      speak(text);
    } else if (!aiEnabled) {
      const data = cardMeanings[cardName];
      if (data) {
        const meaning = isReversed ? data.reversedMeaning : data.meaning;
        speak(`${cardName}. ${meaning} ${data.symbolism}`);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isComplete]);

  const showActions = isComplete && (onDrawClarifying || onDrawAgain);

  return (
    <motion.div
      className="ClarifyingReading"
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1], delay: 0.6 }}
    >
      <div ref={topRef} />

      <div className="ClarifyingReading-label">
        {translate("clarifyingCard")}
      </div>

      {image && (
        <img
          src={image}
          className={`ClarifyingReading-card-img${isReversed ? " ClarifyingReading-card-img--reversed" : ""}`}
          alt={cardName}
        />
      )}

      <div className="ClarifyingReading-header">
        <h2 className="ClarifyingReading-card-name">
          {cardName}
          {isReversed && (
            <span className="ClarifyingReading-reversed">
              {" "}
              · {translate("reversed")}
            </span>
          )}
        </h2>
      </div>

      <div className="ClarifyingReading-divider" />

      {aiEnabled ? (
        <div className="ClarifyingReading-text">
          {text}
          {!isComplete && (
            <span className="ClarifyingReading-cursor" aria-hidden="true">
              ▋
            </span>
          )}
        </div>
      ) : (
        <StaticCardMeaning cardName={cardName} isReversed={isReversed} />
      )}

      {showActions && (
        <motion.div
          className="ClarifyingReading-actions"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.4 }}
        >
          {rateLimitError && (
            <p className="ClarifyingReading-rate-limit">{rateLimitError}</p>
          )}
          {onDrawClarifying && (
            <motion.button
              className="ClarifyingReading-button ClarifyingReading-button--clarify"
              onClick={onDrawClarifying}
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.97 }}
            >
              {translate("drawAnotherClarifying")}
            </motion.button>
          )}
          {onDrawAgain && (
            <motion.button
              className="ClarifyingReading-button"
              onClick={onDrawAgain}
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.97 }}
            >
              {translate("drawAgain")}
            </motion.button>
          )}
        </motion.div>
      )}
    </motion.div>
  );
};

export default ClarifyingReading;
